import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, Clock, Calendar, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { fetchOne, STATIC } from '@/lib/cms';
import SEO from '@/components/site/SEO';
import Markdown, { extractHeadings } from '@/components/site/Markdown';

export default function BlogDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(STATIC.posts.find(x => x.slug === slug) || STATIC.posts[0]);
  useEffect(() => { fetchOne('blog_posts', slug, STATIC.posts).then(d => d && setP(d)); }, [slug]);

  if (!p) return null;
  const others = STATIC.posts.filter(x => x.slug !== p.slug).slice(0, 3);
  // Built from whatever headings actually exist in this post's content, so the
  // sidebar always matches what was written instead of a fixed hardcoded list.
  const headings = extractHeadings(p.content);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: p.title, text: p.excerpt, url }); }
      catch (e) { /* user cancelled the native share sheet — not an error */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  return (
    <>
      <SEO title={p.meta_title || `${p.title} — Slinkflow`} description={p.meta_description || p.excerpt} />
      <article>
        <section className="pt-40 lg:pt-56">
          <div className="mx-auto max-w-[900px] px-6 md:px-10">
            <Link to="/blog" className="text-sm text-ink-500 link-underline">← All posts</Link>
            <p className="mt-8 text-xs uppercase tracking-[0.3em] text-cyan">{p.category}</p>
            <h1 className="mt-4 font-display text-[44px] font-light leading-[1.2] tracking-[-0.02em] md:text-[68px]">
              <TextReveal text={p.title} />
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-ink-500">
              <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {p.published_at}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {p.read_minutes} min read</span>
              <span>By {p.author}</span>
            </div>
          </div>
          <FadeUp delay={0.3} className="mt-12">
            <div className="mx-auto max-w-[1200px] px-6 md:px-10">
              <div className="overflow-hidden rounded-[28px] bg-ink-50">
                <img src={p.cover} alt={p.title} className="aspect-[16/9] w-full object-cover" />
              </div>
            </div>
          </FadeUp>
        </section>

        <section className="py-20">
          <div className="mx-auto grid max-w-[1200px] gap-16 px-6 md:px-10 lg:grid-cols-12">
            <aside className="lg:col-span-3 lg:sticky lg:top-32 self-start">
              {headings.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-ink-400">In this post</p>
                  <ul className="mt-4 space-y-2 text-sm text-ink-700">
                    <li><a href="#introduction" className="link-underline">Introduction</a></li>
                    {headings.map(h => (
                      <li key={h.id} className={h.level === 1 ? '' : 'pl-3'}>
                        <a href={`#${h.id}`} className="link-underline">{h.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={headings.length > 0 ? 'mt-10' : ''}>
                <p className="text-xs uppercase tracking-[0.3em] text-ink-400">Share</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={share} title="Share this post" className="grid h-10 w-10 place-items-center rounded-full border border-ink-100 text-ink-500 hover:text-cyan"><Share2 className="h-4 w-4" /></button>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 prose prose-lg max-w-none">
              <div id="introduction" className="text-[20px] leading-relaxed text-ink-700 scroll-mt-32">
                <p>{p.excerpt}</p>
              </div>
              <div className="mt-10 text-[18px] leading-relaxed text-ink-700">
                <Markdown text={p.content} />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-ink-50 py-24">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10">
            <h2 className="font-display text-[36px] font-light">Related <span className="gradient-text font-medium">reading</span></h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {others.map(o => (
                <Link key={o.id} to={`/blog/${o.slug}`} className="group block overflow-hidden rounded-3xl bg-white">
                  <div className="aspect-[16/10] overflow-hidden"><img src={o.cover} alt={o.title} className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" loading="lazy" /></div>
                  <div className="p-6">
                    <p className="text-[11px] uppercase tracking-widest text-cyan-700">{o.category}</p>
                    <h3 className="mt-2 font-display text-xl text-ink-900">{o.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
